import { ReactNode } from "react";
import CheckIcon from "@mui/icons-material/Check";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";

import { useTranslation } from "@/lib/i18n";
import { useAdminDashboard, usePublishDashboardMutation } from "@/features/admin/hooks";
import { If, confirm, showToast } from "@/features/shared/components";

type Props = {
  open: boolean;
  onClose: () => void;
};

const DashboardPublicationDialog = ({ open, onClose }: Props) => {
  const { t } = useTranslation();
  const publishDashboardMutation = usePublishDashboardMutation();
  const dashboard = useAdminDashboard();

  const hasUnpublishedChanges = dashboard.has_unpublished_changes;

  const onPublish = async () => {
    const confirmed = await confirm(
      t("admin.publication.confirm.title"),
      t("admin.publication.confirm.body"),
    );

    if (!confirmed) {
      return;
    }

    publishDashboardMutation.mutate(dashboard.id, {
      onSuccess: () => {
        showToast(t("admin.messages.dashboard_published"));
        onClose();
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t("admin.publication.title")}</DialogTitle>
      <DialogContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={6}
          sx={{ py: 2, px: "2px" }}
        >
          <If condition={hasUnpublishedChanges}>
            <Box>
              <Typography fontSize={18}>
                {t("admin.publication.publish_changes")}
                <small>
                  <br />
                  {t("admin.publication.publish_changes_subtitle")}
                </small>
              </Typography>
            </Box>
            <Button
              size="large"
              onClick={onPublish}
              color="success"
              variant="outlined"
              disabled={publishDashboardMutation.isPending}
            >
              {t("admin.publication.button")}
            </Button>
          </If>
          <If condition={!hasUnpublishedChanges}>
            <PublicationMessage
              icon={<CheckIcon color="success" sx={{ fontSize: 18, mr: 1 }} />}
              text={t("admin.publication.no_changes")}
              color="#1b5e20"
            />
          </If>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default DashboardPublicationDialog;

const PublicationMessage = ({
  color,
  text,
  icon,
}: {
  color?: string;
  text: string;
  icon?: ReactNode;
}) => (
  <Stack direction="row" alignItems="center" gap={0}>
    {icon}
    <Typography fontSize={18} sx={{ color }}>
      {text}
    </Typography>
  </Stack>
);
